import { useState } from "react";

// Deterministic colour set — identical to the previous file-private copies in
// TranscriptViewPage.tsx and transcript-display.tsx so existing colours do not
// shift after consolidation.
export const AVATAR_COLORS = [
  "bg-blue-500",
  "bg-green-500",
  "bg-purple-500",
  "bg-orange-500",
  "bg-pink-500",
  "bg-teal-500",
  "bg-indigo-500",
  "bg-rose-500",
];

function hashStringToIndex(str: string, max: number): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash) % max;
}

export interface SpeakerAvatarProps {
  /** Speaker display name. Used for alt text, hash-derived colour, and the initial-letter fallback. */
  name: string;
  /** Optional speaker image URL. May be null, undefined, empty, or a URL that fails to load. */
  imageUrl?: string | null;
  /** Avatar size. Default `"md"` (h-7 w-7 text-xs). */
  size?: "sm" | "md" | "lg";
  /**
   * Override Tailwind class for the initials-circle background colour.
   * When provided, replaces the hash-derived AVATAR_COLORS class. Used by
   * transcription-container / ResultView to keep the legacy badge colour scheme.
   */
  className?: string;
}

/**
 * Speaker avatar with L14 three-state image fallback:
 *   (a) `imageUrl` truthy AND not yet errored → render <img>
 *   (b) <img> fires onError → swap to initials (state goes truthy)
 *   (c) `imageUrl` empty / null / undefined → render initials immediately
 *
 * Initials use the speaker's first character (uppercased) on a deterministic
 * colour derived from a hash of `name`, so the same speaker always lands on
 * the same colour across the UI.
 */
export function SpeakerAvatar({
  name,
  imageUrl,
  size = "md",
  className,
}: SpeakerAvatarProps) {
  const [hasError, setHasError] = useState(false);

  const sizeClasses =
    size === "sm"
      ? "h-6 w-6 text-[10px]"
      : size === "lg"
      ? "h-10 w-10 text-sm"
      : "h-7 w-7 text-xs";

  const colorIndex = hashStringToIndex(name || "", AVATAR_COLORS.length);
  const bgColor = className ?? AVATAR_COLORS[colorIndex];
  const initial = (name?.charAt(0) ?? "?").toUpperCase();

  // State (a): URL is set and has not yet errored.
  if (imageUrl && !hasError) {
    return (
      <img
        src={imageUrl}
        alt={name}
        className={`${sizeClasses} shrink-0 rounded-full object-cover shadow-sm ring-1 ring-gray-200`}
        style={{ filter: "sepia(15%) saturate(120%) brightness(105%)" }}
        onError={() => setHasError(true)}
      />
    );
  }

  // States (b) and (c): image errored OR no URL — render initials.
  return (
    <div
      className={`${sizeClasses} ${bgColor} flex shrink-0 items-center justify-center rounded-full font-semibold text-white shadow-sm`}
    >
      {initial}
    </div>
  );
}
